import {
  SKELETON_BLOCK_HEIGHT_LIMITS,
  SKELETON_BLOCK_WIDTH_LIMITS,
  SKELETON_FALLBACK_ITEM_WIDTH,
  SKELETON_LAYOUT_CELL_CLASS,
  SKELETON_LAYOUT_ITEM_CLASS,
  SKELETON_LAYOUT_ROOT_CLASS,
  SKELETON_LAYOUT_ROW_CLASS,
  SKELETON_REPEAT_COUNT_LIMITS,
  SKELETON_SPACING_LIMITS,
  SKELETON_TABLE_COLUMN_LIMITS,
  SKELETON_TABLE_FALLBACK_ROWS,
  SKELETON_TABLE_ROW_LIMITS,
  SkeletonBlockAlignment,
  SkeletonBlockConfig,
  SkeletonBlockPlacement,
  SkeletonBlockShape,
  SkeletonCell,
  SkeletonLayoutConfig,
  SkeletonLayoutRenderOptions,
  SkeletonListLayoutConfig,
  SkeletonOrientation,
  SkeletonRowConfig,
  SkeletonSizeUnit,
  SkeletonTableLayoutConfig,
  SkeletonViewType,
} from './skeleton-layout.type';

/** Shapes as they come back off a persisted project config: anything may be missing. */
export type PersistedSkeletonBlock = Partial<SkeletonBlockConfig>;
export type PersistedSkeletonRow = Partial<Omit<SkeletonRowConfig, 'blocks'>> & {
  blocks?: PersistedSkeletonBlock[];
};

/**
 * A persisted config carries whichever view's keys were saved — and, for a project published
 * before table designs existed, no view type at all. Both views' keys are optional here so a
 * config read back mid-switch can be read without a cast.
 */
export type PersistedSkeletonLayoutConfig = {
  enabled?: boolean;
  viewType?: SkeletonViewType | string;
  repeatCount?: number;
  itemGap?: number;
  rows?: PersistedSkeletonRow[];
  cell?: PersistedSkeletonBlock;
  randomiseWidth?: boolean;
  randomiseHeight?: boolean;
};

/** Sizes are unit-bearing, so keep two decimals — `0.5rem` must not round away to `1rem`. */
function clamp(value: unknown, { min, max }: { min: number; max: number }, fallback: number): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return fallback;
  }
  return Math.min(Math.max(Math.round(numeric * 100) / 100, min), max);
}

function clampInt(value: unknown, limits: { min: number; max: number }, fallback: number): number {
  return Math.round(clamp(value, limits, fallback));
}

function enumValue<T extends Record<string, string>>(
  source: T,
  value: unknown,
  fallback: T[keyof T],
): T[keyof T] {
  return Object.values(source).includes(value as string) ? (value as T[keyof T]) : fallback;
}

export function createSkeletonBlock(overrides: Partial<SkeletonBlockConfig> = {}): SkeletonBlockConfig {
  return {
    shape: SkeletonBlockShape.LINE,
    width: 120,
    widthUnit: SkeletonSizeUnit.PX,
    height: 14,
    heightUnit: SkeletonSizeUnit.PX,
    verticalAlign: SkeletonBlockAlignment.CENTER,
    placement: SkeletonBlockPlacement.LEFT,
    ...overrides,
  };
}

export function createSkeletonRow(overrides: Partial<SkeletonRowConfig> = {}): SkeletonRowConfig {
  return {
    gap: 12,
    offsetLeft: 0,
    marginTop: 0,
    blocks: [createSkeletonBlock()],
    ...overrides,
  };
}

export function createDefaultSkeletonLayoutConfig(
  overrides: Partial<Omit<SkeletonListLayoutConfig, 'viewType'>> = {},
): SkeletonListLayoutConfig {
  return {
    enabled: true,
    viewType: SkeletonViewType.LIST,
    repeatCount: 3,
    itemGap: 25,
    randomiseWidth: false,
    randomiseHeight: false,
    rows: [createSkeletonRow()],
    ...overrides,
  };
}

export function createDefaultSkeletonTableLayoutConfig(
  overrides: Partial<Omit<SkeletonTableLayoutConfig, 'viewType'>> = {},
): SkeletonTableLayoutConfig {
  return {
    enabled: true,
    viewType: SkeletonViewType.TABLE,
    cell: createSkeletonBlock({ width: 100, height: 16 }),
    randomiseWidth: true,
    randomiseHeight: false,
    ...overrides,
  };
}

/**
 * The design a view is given. A widget's own view type is what decides, so it can be passed
 * straight in; anything that is not a table is designed as a list.
 */
export function resolveSkeletonViewType(viewType: string | undefined | null): SkeletonViewType {
  return viewType === SkeletonViewType.TABLE ? SkeletonViewType.TABLE : SkeletonViewType.LIST;
}

export function isTableSkeletonView(viewType: string | undefined | null): boolean {
  return resolveSkeletonViewType(viewType) === SkeletonViewType.TABLE;
}

/**
 * What the widget contributes to the markup, read off its attributes. Both the runtime widget and
 * the designer's preview resolve it from here, so a design cannot look one way in the designer and
 * another in the app. Widgets keep `orientation` under `widgetProperties` and the carousel item
 * width on the attributes root, so both places are tried.
 */
export function resolveSkeletonRenderOptions(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any> | undefined | null,
): SkeletonLayoutRenderOptions {
  const widgetProperties = attributes?.['widgetProperties'];
  return {
    orientation: widgetProperties?.['orientation'] ?? attributes?.['orientation'],
    itemWidth: attributes?.['carouselItemWidth'] ?? widgetProperties?.['carouselItemWidth'],
    itemWidthUnit:
      attributes?.['carouselItemWidthUnit'] ?? widgetProperties?.['carouselItemWidthUnit'],
  };
}

/** Narrows a config to the table design, so its cell can be read without a cast. */
export function isTableSkeletonConfig(
  config: SkeletonLayoutConfig | undefined | null,
): config is SkeletonTableLayoutConfig {
  return !!config && isTableSkeletonView(config.viewType);
}

/**
 * Coerces a persisted (and possibly partial or older) config into a complete, in-range config for
 * the view it was saved under. Persisted project configs are long-lived JSONB blobs, so never
 * trust their shape.
 */
export function normaliseSkeletonLayoutConfig(
  config: PersistedSkeletonLayoutConfig | undefined | null,
  fallback: SkeletonLayoutConfig = createDefaultSkeletonLayoutConfig(),
): SkeletonLayoutConfig {
  if (!config || typeof config !== 'object') {
    return fallback;
  }
  return normaliseSkeletonLayoutConfigFor(resolveSkeletonViewType(config.viewType), config, fallback);
}

/**
 * Coerces a persisted config into the shape a given view needs, dropping the other view's keys.
 * Call it with the widget's own view type: a design saved for the view it is no longer in is
 * replaced by that view's defaults, rather than leaving keys behind that nothing reads.
 */
export function normaliseSkeletonLayoutConfigFor(
  viewType: SkeletonViewType,
  config: PersistedSkeletonLayoutConfig | undefined | null,
  fallback?: SkeletonLayoutConfig,
): SkeletonLayoutConfig {
  return viewType === SkeletonViewType.TABLE
    ? normaliseSkeletonTableLayoutConfig(config, fallback)
    : normaliseSkeletonListLayoutConfig(config, fallback);
}

function normaliseSkeletonListLayoutConfig(
  config: PersistedSkeletonLayoutConfig | undefined | null,
  fallbackConfig?: SkeletonLayoutConfig,
): SkeletonListLayoutConfig {
  const fallback =
    fallbackConfig && !isTableSkeletonView(fallbackConfig.viewType)
      ? (fallbackConfig as SkeletonListLayoutConfig)
      : createDefaultSkeletonLayoutConfig();

  const rows = Array.isArray(config?.rows) ? config.rows : [];

  return {
    enabled: config?.enabled ?? fallback.enabled,
    viewType: SkeletonViewType.LIST,
    repeatCount: clampInt(config?.repeatCount, SKELETON_REPEAT_COUNT_LIMITS, fallback.repeatCount),
    itemGap: clamp(config?.itemGap, SKELETON_SPACING_LIMITS, fallback.itemGap),
    randomiseWidth: config?.randomiseWidth ?? fallback.randomiseWidth,
    randomiseHeight: config?.randomiseHeight ?? fallback.randomiseHeight,
    rows: rows.length ? rows.map(normaliseSkeletonRow) : fallback.rows,
  };
}

function normaliseSkeletonTableLayoutConfig(
  config: PersistedSkeletonLayoutConfig | undefined | null,
  fallbackConfig?: SkeletonLayoutConfig,
): SkeletonTableLayoutConfig {
  const fallback = isTableSkeletonConfig(fallbackConfig as SkeletonLayoutConfig)
    ? (fallbackConfig as SkeletonTableLayoutConfig)
    : createDefaultSkeletonTableLayoutConfig();

  return {
    enabled: config?.enabled ?? fallback.enabled,
    viewType: SkeletonViewType.TABLE,
    cell: normaliseSkeletonBlock(config?.cell ?? fallback.cell, true),
    randomiseWidth: config?.randomiseWidth ?? fallback.randomiseWidth,
    randomiseHeight: config?.randomiseHeight ?? fallback.randomiseHeight,
  };
}

function normaliseSkeletonRow(row: PersistedSkeletonRow): SkeletonRowConfig {
  const blocks = Array.isArray(row?.blocks) ? row.blocks : [];
  return {
    gap: clamp(row?.gap, SKELETON_SPACING_LIMITS, 12),
    offsetLeft: clamp(row?.offsetLeft, SKELETON_SPACING_LIMITS, 0),
    marginTop: clamp(row?.marginTop, SKELETON_SPACING_LIMITS, 0),
    blocks: blocks.length ? blocks.map((block) => normaliseSkeletonBlock(block)) : [createSkeletonBlock()],
  };
}

function normaliseSkeletonBlock(block: PersistedSkeletonBlock, isCell = false): SkeletonBlockConfig {
  const shape = enumValue(SkeletonBlockShape, block?.shape, SkeletonBlockShape.LINE);
  return {
    shape,
    width: clamp(block?.width, SKELETON_BLOCK_WIDTH_LIMITS, 120),
    widthUnit: enumValue(SkeletonSizeUnit, block?.widthUnit, SkeletonSizeUnit.PX),
    height: clamp(block?.height, SKELETON_BLOCK_HEIGHT_LIMITS, shape === SkeletonBlockShape.CIRCLE ? 40 : 14),
    heightUnit: enumValue(SkeletonSizeUnit, block?.heightUnit, SkeletonSizeUnit.PX),
    verticalAlign: isCell
      ? SkeletonBlockAlignment.CENTER
      : enumValue(SkeletonBlockAlignment, block?.verticalAlign, SkeletonBlockAlignment.CENTER),
    placement: isCell
      ? SkeletonBlockPlacement.LEFT
      : enumValue(SkeletonBlockPlacement, block?.placement, SkeletonBlockPlacement.LEFT),
  };
}

/**
 * A row centres its blocks, so only a block placed anywhere else needs saying. Leaving the centre
 * default out keeps the markup of every design saved before alignment existed byte-identical.
 */
function verticalAlignStyle(block: SkeletonBlockConfig): string[] {
  return block.verticalAlign === SkeletonBlockAlignment.CENTER ? [] : [`align-self:${block.verticalAlign}`];
}

/**
 * The gaps that carry a block to its side of the row. An `auto` margin eats the space the row has
 * left over, so the group after it is carried along: one on the first block placed right pins it
 * and everything after to the right edge, and one either side of a centred group balances it
 * between the groups beside it. A row of blocks that are all placed left has no auto margin at all,
 * so they sit against the row's leading edge.
 */
function placementStyles(blocks: SkeletonBlockConfig[]): string[][] {
  const firstCentre = blocks.findIndex((block) => block.placement === SkeletonBlockPlacement.CENTER);
  const lastCentre = blocks.reduce(
    (last, block, index) => (block.placement === SkeletonBlockPlacement.CENTER ? index : last),
    -1,
  );
  const firstRight = blocks.findIndex((block) => block.placement === SkeletonBlockPlacement.RIGHT);

  return blocks.map((_, index) => {
    const style: string[] = [];
    // with a centred group present, its trailing margin is what carries the right-hand group over
    if (index === firstCentre || (firstCentre < 0 && index === firstRight)) {
      style.push('margin-left:auto');
    }
    if (index === lastCentre) {
      style.push('margin-right:auto');
    }
    return style;
  });
}

/**
 * How a line's size varies from one repeat of the item to the next. `next()` hands out the seed
 * for the placeholder about to be rendered — a running count, so every line down the list gets its
 * own variation and the same design always renders the same way.
 */
type SkeletonVariation = {
  randomiseWidth: boolean;
  randomiseHeight: boolean;
  next: () => number;
};

function createVariation(config: SkeletonListLayoutConfig): SkeletonVariation {
  let seed = 0;
  return {
    randomiseWidth: config.randomiseWidth,
    randomiseHeight: config.randomiseHeight,
    next: () => seed++,
  };
}

function blockStyle(
  block: SkeletonBlockConfig,
  placement: string[] = [],
  variation?: SkeletonVariation,
): string {
  const isCircle = block.shape === SkeletonBlockShape.CIRCLE;
  // A circle is an avatar: it keeps the size it was designed at however much the lines vary, so
  // only a line takes a seed — and taking one is what keeps the seeds in step across repeats.
  const seed = isCircle ? undefined : variation?.next();

  const heightValue =
    seed === undefined ? block.height : randomisedSize(block.height, !!variation?.randomiseHeight, seed, 2);
  const widthValue =
    seed === undefined ? block.width : randomisedSize(block.width, !!variation?.randomiseWidth, seed, 1);

  const height = `${heightValue}${block.heightUnit}`;
  // a circle takes its width from its height, so it stays round whatever unit is in use
  const width = isCircle ? height : `${widthValue}${block.widthUnit}`;

  const style = [
    'display:inline-block',
    'flex:0 0 auto',
    `width:${width}`,
    `height:${height}`,
    'overflow:hidden',
    ...verticalAlignStyle(block),
    ...placement,
  ];

  // Colour and corner come from the project's global styling, through `.skeleton_item`. Only a
  // circle overrides the corner, because being round is its shape rather than a designed radius.
  if (isCircle) {
    style.push('border-radius:50%');
  }

  return style.join(';');
}

function blockMarkup(
  block: SkeletonBlockConfig,
  placement: string[] = [],
  variation?: SkeletonVariation,
): string {
  return `<span class="skeleton_item" style="${blockStyle(block, placement, variation)}"></span>`;
}

function rowStyle(row: SkeletonRowConfig): string {
  return [
    'display:flex',
    'align-items:center',
    'width:100%',
    'box-sizing:border-box',
    // where the blocks sit along the row is `placement`'s to decide, block by block
    'justify-content:flex-start',
    `gap:${row.gap}px`,
    `padding-left:${row.offsetLeft}px`,
    `margin-top:${row.marginTop}px`,
  ].join(';');
}

function isHorizontal(options: SkeletonLayoutRenderOptions | undefined): boolean {
  return options?.orientation === SkeletonOrientation.HORIZONTAL;
}

/**
 * The width one repeat takes when they run side by side, sized as the widget sizes its own items.
 * It arrives from a persisted widget attribute, so it is clamped and its unit validated before it
 * reaches a style attribute — nothing a builder typed is interpolated verbatim.
 */
function horizontalItemWidth(
  config: SkeletonListLayoutConfig,
  options: SkeletonLayoutRenderOptions | undefined,
): string {
  // A single repeat fills the row, exactly as the carousel gives a lone item the full width.
  if (config.repeatCount === 1) {
    return '100%';
  }
  const width = clamp(options?.itemWidth, SKELETON_BLOCK_WIDTH_LIMITS, SKELETON_FALLBACK_ITEM_WIDTH);
  const unit = enumValue(SkeletonSizeUnit, options?.itemWidthUnit, SkeletonSizeUnit.PERCENT);
  return `${width}${unit}`;
}

function itemStyle(
  config: SkeletonListLayoutConfig,
  isLast: boolean,
  options?: SkeletonLayoutRenderOptions,
): string {
  if (isHorizontal(options)) {
    // The root holds the gap, so a horizontal repeat only has to hold its own width and refuse to
    // be shrunk by the flex row — the same shape the carousel gives its items.
    return [
      'display:flex',
      'flex-direction:column',
      `flex:0 0 ${horizontalItemWidth(config, options)}`,
      'box-sizing:border-box',
    ].join(';');
  }

  const style = ['display:flex', 'flex-direction:column', 'width:100%', 'box-sizing:border-box'];

  if (!isLast) {
    style.push(`margin-bottom:${config.itemGap}px`);
  }

  return style.join(';');
}

/**
 * Horizontally the repeats run in a flex row that is clipped rather than scrolled: a loading state
 * has nothing to scroll to, and the part-visible item at the edge is what the real carousel shows.
 */
function rootStyle(config: SkeletonListLayoutConfig, options?: SkeletonLayoutRenderOptions): string {
  if (!isHorizontal(options)) {
    return 'width:100%';
  }
  return ['display:flex', 'width:100%', 'overflow:hidden', `gap:${config.itemGap}px`].join(';');
}

/** One skeleton row per row of data the table is about to show. */
export function deriveSkeletonTableRowCount(input: { pagingEnabled?: boolean; pageSize?: number }): number {
  const pagedRows = input.pagingEnabled && input.pageSize ? Number(input.pageSize) : undefined;
  return clampInt(
    pagedRows ?? SKELETON_TABLE_FALLBACK_ROWS,
    SKELETON_TABLE_ROW_LIMITS,
    SKELETON_TABLE_FALLBACK_ROWS,
  );
}

/** One skeleton column per column the table already shows. */
export function deriveSkeletonTableColumnCount(visibleColumnCount: number | undefined): number {
  return clampInt(
    visibleColumnCount ?? SKELETON_TABLE_COLUMN_LIMITS.min,
    SKELETON_TABLE_COLUMN_LIMITS,
    SKELETON_TABLE_COLUMN_LIMITS.min,
  );
}

/**
 * Deterministic pseudo-random in [0, 1) derived from the cell index. `Math.random()` would
 * reshuffle every cell on each change-detection pass, making a loading table flicker.
 */
function seededFraction(index: number, salt: number): number {
  const value = Math.sin((index + 1) * 12.9898 + salt * 78.233) * 43758.5453;
  return value - Math.floor(value);
}

/**
 * Randomised sizes are fractions of the size the app builder typed, so a ragged edge still reads
 * as part of the same design. Scaling the configured size rather than snapping to a ladder of
 * pixel values is what lets small sizes — and em/rem ones — vary at all.
 */
const SKELETON_RANDOMISE_STEPS = [0.55, 0.7, 0.85, 1] as const;

function randomisedSize(value: number, randomise: boolean, index: number, salt: number): number {
  if (!randomise) {
    return value;
  }
  const step =
    SKELETON_RANDOMISE_STEPS[Math.floor(seededFraction(index, salt) * SKELETON_RANDOMISE_STEPS.length)];
  return Math.max(0.01, Math.round(value * step * 100) / 100);
}

/**
 * Builds one cell descriptor per skeleton cell, in row-major order.
 *
 * A table's skeleton is not designed cell by cell: the rows come from the page size and the
 * columns from the columns the widget already shows, so every cell holds the same configured
 * block, optionally varied in size. Size is all a cell carries — the shade and the corner come
 * from global styling, through the `.skeleton_item` class every cell wears.
 */
export function buildSkeletonTableCells(
  config: PersistedSkeletonLayoutConfig | undefined | null,
  rowCount: number,
  columnCount: number,
): SkeletonCell[] {
  // Read as a table design whatever view it was saved under: reaching here means a table is
  // rendering it, and a design saved for the list view has no cell to take.
  const table = normaliseSkeletonLayoutConfigFor(SkeletonViewType.TABLE, config) as SkeletonTableLayoutConfig;
  const cellCount =
    deriveSkeletonTableRowCount({ pagingEnabled: true, pageSize: rowCount }) *
    deriveSkeletonTableColumnCount(columnCount);
  const isCircle = table.cell.shape === SkeletonBlockShape.CIRCLE;

  return Array.from({ length: cellCount }, (_, index) => {
    const height = `${randomisedSize(table.cell.height, table.randomiseHeight, index, 2)}${
      table.cell.heightUnit
    }`;
    return {
      // a circle cell is square, taking its width from its height whatever unit is in use
      width: isCircle
        ? height
        : `${randomisedSize(table.cell.width, table.randomiseWidth, index, 1)}${table.cell.widthUnit}`,
      height,
    };
  });
}

/** The cell for a given row and column, matching `buildSkeletonTableCells` ordering. */
export function getSkeletonTableCell(
  cells: SkeletonCell[],
  rowIndex: number,
  columnIndex: number,
  columnCount: number,
): SkeletonCell | undefined {
  return cells[rowIndex * Math.max(1, columnCount) + columnIndex];
}

/**
 * Renders a table design as plain HTML, for the app builder's preview. The live table draws its
 * own rows and cells instead, so the blocks line up with the columns it is already showing.
 */
export function generateSkeletonTableTemplate(
  config: PersistedSkeletonLayoutConfig | undefined | null,
  rowCount: number,
  columnCount: number,
): string {
  const columns = deriveSkeletonTableColumnCount(columnCount);
  const cells = buildSkeletonTableCells(config, rowCount, columns);
  if (!cells.length) {
    return '';
  }

  const cellsMarkup = cells
    .map((cell) => {
      const style = [
        'display:inline-block',
        `width:${cell.width}`,
        `height:${cell.height}`,
        'overflow:hidden',
      ].join(';');

      return `<span class="${SKELETON_LAYOUT_CELL_CLASS} skeleton_item" style="${style}"></span>`;
    })
    .join('');

  const gridStyle = [
    'display:grid',
    `grid-template-columns:repeat(${columns},minmax(0,1fr))`,
    'align-items:center',
    'gap:12px',
    'width:100%',
    'box-sizing:border-box',
  ].join(';');

  return `<div class="${SKELETON_LAYOUT_ROOT_CLASS}" aria-hidden="true" style="${gridStyle}">${cellsMarkup}</div>`;
}

/**
 * Renders a skeleton design as the plain HTML stored on the widget's loading template.
 *
 * The markup carries no handlebars, so `filterIfHandlebarNotRendered` keeps it, and it leans on
 * the global `.skeleton_item` class for the shimmer, the shade and the corner radius while
 * setting every dimension inline (`.skeleton_item` itself resolves to `height: auto`, which
 * would otherwise collapse).
 *
 * Table designs render nothing here — a table draws its skeleton as real rows and cells.
 */
export function generateSkeletonTemplate(
  config: PersistedSkeletonLayoutConfig | undefined | null,
  options?: SkeletonLayoutRenderOptions,
): string {
  const resolved = normaliseSkeletonLayoutConfig(config);
  if (isTableSkeletonConfig(resolved)) {
    return '';
  }

  const normalised = resolved;
  if (!normalised.enabled || !normalised.rows.length) {
    return '';
  }

  // Every repeat is rendered in turn rather than one item's markup being copied, because a design
  // that varies its sizes has to draw each repeat with the next seed in the run.
  const variation = createVariation(normalised);

  const itemsMarkup = Array.from({ length: normalised.repeatCount }, (_, index) => {
    const isLast = index === normalised.repeatCount - 1;
    const rowsMarkup = normalised.rows
      .map((row) => {
        const placements = placementStyles(row.blocks);
        const blocksMarkup = row.blocks
          .map((block, blockIndex) => blockMarkup(block, placements[blockIndex], variation))
          .join('');
        return `<div class="${SKELETON_LAYOUT_ROW_CLASS}" style="${rowStyle(row)}">${blocksMarkup}</div>`;
      })
      .join('');

    return `<div class="${SKELETON_LAYOUT_ITEM_CLASS}" style="${itemStyle(
      normalised,
      isLast,
      options,
    )}">${rowsMarkup}</div>`;
  }).join('');

  return `<div class="${SKELETON_LAYOUT_ROOT_CLASS}" aria-hidden="true" style="${rootStyle(
    normalised,
    options,
  )}">${itemsMarkup}</div>`;
}

/**
 * Resolves the markup a listing widget renders while it is loading.
 *
 * The saved design is the source of truth, so both the live app and the app-builder canvas
 * preview render from the same config; the widget's own loading template is the fallback used
 * whenever no skeleton is applied. Call this wherever a widget compiles its loading template.
 *
 * Pass the widget's current `viewType`: a table draws its skeleton as real rows and cells, so
 * there is nothing to put on its loading template and the fallback is what belongs there. Left
 * out, only the saved design's own view type is considered.
 *
 * Pass `options` for a widget that lays its items out horizontally, so the repeats run the way the
 * widget's own items do instead of stacking full width.
 */
export function resolveSkeletonLoadingTemplate(
  config: PersistedSkeletonLayoutConfig | undefined | null,
  fallbackTemplate: string,
  viewType?: string,
  options?: SkeletonLayoutRenderOptions,
): string {
  if (!config?.enabled || isTableSkeletonView(viewType)) {
    return fallbackTemplate;
  }
  return generateSkeletonTemplate(config, options) || fallbackTemplate;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function readSkeletonConfigPath<T = any>(source: any, path: string): T | undefined {
  if (!source || !path) {
    return undefined;
  }
  return path.split('.').reduce((accumulator, key) => accumulator?.[key], source) as T | undefined;
}

/**
 * Writes `value` at `path`, cloning every object along the way so change detection on the
 * widget attributes still sees a new reference.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function writeSkeletonConfigPath<T extends Record<string, any>>(
  source: T,
  path: string,
  value: unknown,
): T {
  const [key, ...rest] = path.split('.');
  if (!key) {
    return source;
  }

  return {
    ...source,
    [key]: rest.length ? writeSkeletonConfigPath(source?.[key] ?? {}, rest.join('.'), value) : value,
  };
}
